import React, { useState, useEffect, useRef } from "react";
import { Search, X, User as UserIcon } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

function displayNameOf(u = {}) {
  return (
    u.displayName ||
    u.name ||
    [u.firstName, u.lastName].filter(Boolean).join(" ") ||
    u.email ||
    "User"
  );
}

/**
 * UserPickerField (SOUND-504) — debounced Sound-team (internal) user picker.
 * Used to set a function's default owner. Emits `{ userId, displayName }`
 * (or empty on clear). Mirrors CompanyPickerField's selected-chip / search
 * pattern in the ticketing grammar.
 */
export default function UserPickerField({ value, valueLabel, onChange }) {
  const auth = useAuth();
  const companyId = useEffectiveCompanyId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!auth?.user) return;
      setLoading(true);
      try {
        const p = new URLSearchParams();
        p.set("userType", "internal");
        p.set("limit", "10");
        if (query) p.set("search", query);
        const res = await makeApiRequest(
          "get",
          `${apiRoutes.getCompanyUsers}?${p.toString()}`,
          auth.user,
          companyId
        );
        setResults(res.data?.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, companyId]);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
        <UserIcon className="h-4 w-4 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{valueLabel || value}</span>
        <button
          type="button"
          onClick={() => onChange?.({ userId: "", displayName: "" })}
          className="btn btn-ghost h-6 w-6 p-0"
          aria-label="Clear default owner"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          className="input pl-9"
          placeholder="Search Sound team…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
          {loading ? (
            <div className="py-3 text-center text-xs text-muted-foreground">Searching…</div>
          ) : results.length === 0 ? (
            <div className="py-3 text-center text-xs text-muted-foreground">
              {query ? "No team members found." : "Start typing to search."}
            </div>
          ) : (
            results.map((u) => (
              <button
                key={u.id || u._id}
                type="button"
                onClick={() => {
                  onChange?.({ userId: u.id || u._id, displayName: displayNameOf(u) });
                  setOpen(false);
                  setQuery("");
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{displayNameOf(u)}</span>
                <span className="shrink-0 truncate text-xs text-muted-foreground">{u.email}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
